<script setup lang="ts">
import {EmailAddress, ExtendedUser} from "@/types";
import BaseLayout from "@/layouts/BaseLayout.vue";
import ProfileLayout from "@/Pages/settings/layouts/ProfileLayout.vue";
import TextField from "@/components/forms/TextField.vue";
import { Head } from '@inertiajs/vue3'
import {router, useForm} from "@inertiajs/vue3";
import {route} from "ziggy-js";

interface Props {
    user: ExtendedUser,
    emails: Array<EmailAddress>
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout,
})

const form = useForm({
    email: '',
})

const primaryForm = useForm({
    email: props.user.email,
})

const submit = () => {
    form.post(route('settings.emails.store'), {
        preserveScroll: true,
        onSuccess: () => {
            form.reset()
        }
    })
}

const remove = (email: EmailAddress) => {
    if (confirm('Are you sure you want to remove this email from your account? This cannot be undone.')) {
        router.delete(route('settings.emails.destroy', {email: email.email}), {
            preserveScroll: true,
        })
    }
}

const setAsPrimary = () => {
    primaryForm.put(route('settings.emails.update'), {
        preserveScroll: true,
    })
}
</script>

<template>
    <Head title="Email addresses" />
    <profile-layout
        :user="props.user"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            Emails
        </h1>
        <v-divider class="my-5" />
        <v-list
            border="sm"
            rounded="lg"
        >
            <v-list-item
                v-for="(email, index) in props.emails"
                :key="email.email"
                :class="index < props.emails.length - 1 ? 'border-b' : ''"
            >
                <v-list-item-title class="font-weight-bold mt-2">
                    {{ email.email }}
                    <span
                        v-if="props.user.email === email.email"
                        class="text-body-1"
                    >–</span>
                    <span
                        v-if="props.user.email === email.email"
                        class="ml-1 text-success text-body-2 font-weight-bold"
                    >Primary</span>
                    <default-btn
                        v-if="email.email !== props.user.email"
                        class="border-sm text-error px-1 float-end"
                        rounded="lg"
                        size="x-small"
                        icon="mdi-delete-outline"
                        @click="remove(email)"
                    />
                    <v-list-item-subtitle
                        v-if="props.user.email === email.email"
                        class="text-caption text-grey-darken-2"
                    >
                        This email will be used for account-related notifications and can also be used for password resets.
                    </v-list-item-subtitle>
                    <ul
                        class="mx-7 lh-1-5 mt-3 text-start text-grey-darken-3 text-body-2"
                    >
                        <li
                            v-if="email.verified_at === null"
                            class="pl-1 mt-3"
                        >
                            <span class="font-weight-medium text-unverified">Unverified</span>
                            <p class="text-caption">
                                Unverified email addresses cannot be used as primary email address.
                            </p>
                        </li>
                        <li
                            v-if="props.user.email === email.email"
                            class="pl-1 mt-3"
                        >
                            Visible to everyone
                            <p class="text-caption">
                                This email will be visible to everyone on your profile.
                            </p>
                        </li>
                        <li
                            v-if="email.verified_at !== null"
                            class="pl-1 mt-3"
                        >
                            PGP keys
                            <p class="text-caption">
                                PGP keys with the following identities will be automatically verified
                            </p>
                        </li>
                    </ul>
                </v-list-item-title>
            </v-list-item>
        </v-list>
        <v-row class="mt-5">
            <v-col
                cols="12"
                md="6"
            >
                <text-field
                    v-model="form.email"
                    label="Add email address"
                    placeholder="Email address"
                    :errors="form.errors.email"
                >
                    <template #append>
                        <default-btn
                            border="sm"
                            rounded="lg"
                            @click="submit"
                        >
                            Add
                        </default-btn>
                    </template>
                </text-field>
            </v-col>
        </v-row>
        <v-divider />
        <v-row class="mt-5">
            <v-col
                cols="12"
                class="py-0"
            >
                <label class="font-weight-medium">Primary email address</label>
            </v-col>
            <v-col
                cols="12"
                class="pb-0"
            >
                <div class="text-body-2">
                    <code>{{ props.user.email }}</code> will be used for account-related notifications and can be used for password resets.
                </div>
            </v-col>
            <v-col
                cols="12"
                md="6"
            >
                <v-select
                    v-model="primaryForm.email"
                    :items="props.emails.filter(email => email.verified_at !== null)"
                    :hide-selected="true"
                    :hide-no-data="true"
                    item-title="email"
                    item-value="email"
                    variant="outlined"
                    class="mt-2"
                    density="compact"
                    rounded="lg"
                    color="blue"
                >
                    <template #append>
                        <default-btn
                            border="sm"
                            rounded="lg"
                            @click="setAsPrimary"
                        >
                            Save
                        </default-btn>
                    </template>
                </v-select>
            </v-col>
        </v-row>
    </profile-layout>
</template>

<style scoped>
.lh-1-5 {
    line-height: 1.5;
}

.text-unverified {
    color: rgb(154, 103, 0);
}
</style>
